import React, { useState, useEffect } from 'react';
import {
  Typography, Box, TextField, Button, MenuItem, Paper, CircularProgress,
  Stepper, Step, StepLabel, StepConnector, styled, Card, CardContent, Tooltip, useTheme, IconButton
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { createStaff, fetchStaff } from '../slices/staffSlice';
import {
  fetchEmployeeTypes,
} from '../slices/employeeTypesSlice';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../context/ToastContext';
import { motion, AnimatePresence } from 'framer-motion';
import axios from '../api/axiosInstance';
import {
  Person, Work, NoteAdd, Check, KeyboardArrowLeft, KeyboardArrowRight,
  Save, Cancel, Badge, Phone, AttachFile, CloudUpload, CheckCircle, ArrowBack,
  PersonAddAlt1
} from '@mui/icons-material';

const MotionPaper = motion(Paper);

// Custom styled components for enhanced visual appeal (mirrors CreateStudentPage)
const ColorlibConnector = styled(StepConnector)(({ theme }) => ({
  [`&.MuiStepConnector-alternativeLabel`]: {
    top: 23,
    left: 'unset',
    right: 'unset',
    insetInlineStart: 'calc(-50% + 26px)',
    insetInlineEnd: 'calc(50% + 26px)',
  },
  [`&.MuiStepConnector-active`]: {
    [`& .MuiStepConnector-line`]: {
      backgroundImage: 'linear-gradient(95deg, #00BFFF 0%, #4338CA 60%, #5B21F4 100%)',
    },
  },
  [`&.MuiStepConnector-completed`]: {
    [`& .MuiStepConnector-line`]: {
      backgroundImage: 'linear-gradient(95deg, #00BFFF 0%, #4338CA 60%, #5B21F4 100%)',
    },
  },
  [`& .MuiStepConnector-line`]: {
    height: 4,
    border: 0,
    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : '#E5E7EB',
    borderRadius: 4,
  },
}));

const PHONE_CODE_OPTIONS = [
  { value: '972', label: '+972', flag: '🇵🇸' },
  { value: '970', label: '+970', flag: '🇵🇸' }
];

const ColorlibStepIconRoot = styled('div')(({ theme, ownerState }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[700] : '#E5E7EB',
  zIndex: 1,
  color: '#fff',
  width: 50,
  height: 50,
  display: 'flex',
  borderRadius: '50%',
  justifyContent: 'center',
  alignItems: 'center',
  transition: 'all 0.3s ease',
  boxShadow: '0 2px 6px rgba(67,56,202,0.12)',
  ...(ownerState.active && {
    backgroundImage: 'linear-gradient(136deg, #00BFFF 0%, #4338CA 60%, #5B21F4 100%)',
    boxShadow: '0 0 0 4px rgba(67,56,202,0.18), 0 4px 14px rgba(67,56,202,0.4)',
    transform: 'scale(1.12)',
  }),
  ...(ownerState.completed && {
    backgroundImage: 'linear-gradient(136deg, #22C55E 0%, #16A34A 100%)',
    boxShadow: '0 3px 10px rgba(22,163,74,0.35)',
  }),
}));

function ColorlibStepIcon(props) {
  const { active, completed, className, icon } = props;
  const icons = {
    1: <Person />,
    2: <Work />,
    3: <AttachFile />,
    4: <NoteAdd />,
  };

  return (
    <ColorlibStepIconRoot ownerState={{ completed, active }} className={className}>
      {completed ? <Check /> : icons[icon]}
    </ColorlibStepIconRoot>
  );
}

const pageVariants = {
  initial: { opacity: 0, x: 50 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -50 }
};

const steps = ['معلومات شخصية', 'معلومات وظيفية', 'تحميل العقد', 'ملاحظات'];

const fieldSx = {
  fontFamily: 'Cairo, Tajawal, sans-serif',
  '& .MuiInputBase-root, & .MuiInputLabel-root, & .MuiFormHelperText-root': {
    fontFamily: 'Cairo, Tajawal, sans-serif',
  },
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    transition: 'box-shadow 0.2s ease',
    '&:hover': { backgroundColor: 'rgba(67,56,202,0.02)' },
    '&.Mui-focused': {
      boxShadow: '0 0 0 3px rgba(67,56,202,0.12)',
      '& .MuiOutlinedInput-notchedOutline': { borderWidth: 2, borderColor: '#4338CA' },
    },
    '&.Mui-error': {
      '&.Mui-focused': { boxShadow: '0 0 0 3px rgba(220,38,38,0.12)' },
    },
  },
  '& .MuiInputLabel-root.Mui-focused': { color: '#4338CA' },
};

const StepCardHeader = ({ icon, title, subtitle, color, bg, borderColor }) => (
  <Box sx={{
    background: bg,
    px: 3, py: 2,
    display: 'flex', alignItems: 'center', gap: 1.5,
    borderBottom: `1px solid ${borderColor}`,
  }}>
    <Box sx={{
      width: 44, height: 44, borderRadius: '12px',
      background: color,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      boxShadow: `0 4px 12px ${borderColor}`,
      flexShrink: 0,
    }}>
      {icon}
    </Box>
    <Box sx={{ flexGrow: 1 }}>
      <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2, color: '#1E1B4B', fontFamily: 'Cairo, Tajawal, sans-serif' }}>
        {title}
      </Typography>
      <Typography variant="caption" sx={{ color: '#6B7280', fontSize: '0.75rem', fontFamily: 'Cairo, Tajawal, sans-serif' }}>
        {subtitle}
      </Typography>
    </Box>
    <Box sx={{
      px: 1.5, py: 0.4, borderRadius: '8px',
      backgroundColor: borderColor,
      border: `1px solid ${borderColor}`,
    }}>
      <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.7rem', color: '#6B7280', fontFamily: 'Cairo, Tajawal, sans-serif' }}>
        * مطلوب
      </Typography>
    </Box>
  </Box>
);

const CreateStaffPage = () => {
  const theme = useTheme();
  const { items: employeeTypes } = useSelector(state => state.employeeTypes);
  const { list: existingEmployees } = useSelector(state => state.staff);
  const navigate = useNavigate();
  const { showToast } = useToast();
  const dispatch = useDispatch();
  const [activeStep, setActiveStep] = useState(0);
  const [triedNext, setTriedNext] = useState(false);

  const [staff, setStaff] = useState({
    idNumber: '',
    firstName: '',
    lastName: '',
    birthDate: '',
    phone: '',
    phoneCode: '972',
    email: '',
    position: '',
    hireDate: '',
    status: '',
    salary: '',
    workingHoursPerWeek: '',
    contractFile: null,
    notes: '',
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [employeeIdValidating, setEmployeeIdValidating] = useState(false);
  const [employeeIdError, setEmployeeIdError] = useState('');

  // Load existing employees when component mounts
  useEffect(() => {
    if (existingEmployees.length === 0) {
      dispatch(fetchStaff());
    }
    dispatch(fetchEmployeeTypes());
  }, [dispatch, existingEmployees.length]);

  // Debounced employee ID validation
  useEffect(() => {
    const validateEmployeeId = async () => {
      if (!staff.idNumber || staff.idNumber.length < 2) {
        setEmployeeIdError('');
        return;
      }

      setEmployeeIdValidating(true);

      try {
        // First check locally in Redux store
        const duplicateInStore = existingEmployees.find(
          emp => emp.employee_id === staff.idNumber && !emp.is_archived
        );

        if (duplicateInStore) {
          setEmployeeIdError(`معرف الموظف "${staff.idNumber}" موجود بالفعل للموظف ${duplicateInStore.first_name} ${duplicateInStore.last_name}`);
          setEmployeeIdValidating(false);
          return;
        }

        // If not found locally, check with backend for real-time validation
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/employees/`, {
          params: { employee_id: staff.idNumber }
        });

        const duplicateEmployee = response.data.find(
          emp => emp.employee_id === staff.idNumber && !emp.is_archived
        );

        if (duplicateEmployee) {
          setEmployeeIdError(`معرف الموظف "${staff.idNumber}" موجود بالفعل للموظف ${duplicateEmployee.first_name} ${duplicateEmployee.last_name}`);
        } else {
          setEmployeeIdError('');
        }
      } catch (error) {
        console.error('Error validating employee ID:', error);
        // Don't show error to user for validation failures
        setEmployeeIdError('');
      } finally {
        setEmployeeIdValidating(false);
      }
    };

    const debounceTimer = setTimeout(validateEmployeeId, 500);
    return () => clearTimeout(debounceTimer);
  }, [staff.idNumber, existingEmployees]);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (files) {
      const file = files[0];
      setStaff((prev) => ({ ...prev, [name]: file }));
    } else {
      setStaff((prev) => ({ ...prev, [name]: value }));

      // Clear employee ID error when user starts typing
      if (name === 'idNumber') {
        setEmployeeIdError('');
      }
    }
  };

  // Helper validation functions
  const isArabic = (str) => /^[؀-ۿ\s]+$/.test(str);
  const isValidId = (id) => /^\d{9}$/.test(id);
  const isValidPhone = (phone) => /^0\d{9}$/.test(phone); // 10 digits, starts with 0
  const isValidEmail = (email) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const isPositiveNumber = (num) => /^\d+(\.\d+)?$/.test(num) && Number(num) > 0;

  const validateStep = () => {
    const tempErrors = {};
    if (activeStep === 0) {
      if (!staff.idNumber) {
        tempErrors.idNumber = 'رقم الهوية مطلوب';
      } else if (!isValidId(staff.idNumber)) {
        tempErrors.idNumber = 'رقم الهوية يجب أن يتكون من 9 أرقام صحيحة';
      } else if (employeeIdError) {
        tempErrors.idNumber = employeeIdError;
      }

      if (!staff.firstName) tempErrors.firstName = 'الاسم الأول مطلوب';
      else if (!isArabic(staff.firstName)) tempErrors.firstName = 'الاسم الأول يجب أن يكون باللغة العربية فقط';

      if (!staff.lastName) tempErrors.lastName = 'اسم العائلة مطلوب';
      else if (!isArabic(staff.lastName)) tempErrors.lastName = 'اسم العائلة يجب أن يكون باللغة العربية فقط';

      if (!staff.birthDate) tempErrors.birthDate = 'تاريخ الميلاد مطلوب';

      if (!staff.phone) tempErrors.phone = 'رقم الهاتف مطلوب';
      else if (!isValidPhone(staff.phone)) tempErrors.phone = 'رقم الهاتف يجب أن يكون 10 أرقام ويبدأ بـ 0';

      if (staff.email && !isValidEmail(staff.email)) {
        tempErrors.email = 'البريد الإلكتروني غير صحيح';
      }
    }
    if (activeStep === 1) {
      if (!staff.position) tempErrors.position = 'الوظيفة مطلوبة';
      if (!staff.hireDate) tempErrors.hireDate = 'تاريخ التعيين مطلوب';
      if (!staff.salary) tempErrors.salary = 'الراتب مطلوب';
      else if (!isPositiveNumber(staff.salary)) tempErrors.salary = 'الراتب يجب أن يكون رقماً موجباً';
    }
    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleNext = () => {
    setTriedNext(true);

    // Additional check for employee ID validation in progress
    if (activeStep === 0 && (employeeIdValidating || employeeIdError)) {
      if (employeeIdValidating) {
        showToast('جاري التحقق من معرف الموظف، يرجى الانتظار...', 'info');
        return;
      }
      if (employeeIdError) {
        showToast('يرجى حل مشكلة معرف الموظف قبل المتابعة', 'error');
        return;
      }
    }

    if (validateStep()) {
      setActiveStep((prev) => prev + 1);
      setTriedNext(false);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleCancel = () => {
    navigate('/staff');
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (employeeIdError) {
      showToast('لا يمكن حفظ الموظف: معرف الموظف مكرر', 'error');
      return;
    }

    if (validateStep()) {
      setLoading(true);
      const formData = new FormData();
      formData.append('first_name', staff.firstName);
      formData.append('last_name', staff.lastName);
      formData.append('employee_id', staff.idNumber);
      formData.append('birth_date', staff.birthDate);
      formData.append('phone_number', staff.phone);
      formData.append('phone_code', staff.phoneCode);
      formData.append('email', staff.email);
      formData.append('employee_type', staff.position); // this should be the UUID of EmployeeType
      formData.append('start_date', staff.hireDate);
      formData.append('base_salary', staff.salary);
      formData.append('note', staff.notes);
      if (staff.contractFile) {
        formData.append('contract_pdf', staff.contractFile);
      }

      dispatch(createStaff(formData))
        .unwrap()
        .then(() => {
          showToast('تم إنشاء الموظف بنجاح!', 'success');
          navigate('/staff');
        })
        .catch((error) => {
          // Handle specific validation errors from backend
          if (error.employee_id) {
            showToast(Array.isArray(error.employee_id) ? error.employee_id[0] : error.employee_id, 'error');
          } else {
            showToast('فشل في إنشاء الموظف', 'error');
          }
        })
        .finally(() => setLoading(false));
    }
  };

  const isStepComplete = (step) => {
    if (step === 0) {
      return (
        !!staff.idNumber &&
        isValidId(staff.idNumber) &&
        !employeeIdError &&
        !!staff.firstName &&
        isArabic(staff.firstName) &&
        !!staff.lastName &&
        isArabic(staff.lastName) &&
        !!staff.birthDate &&
        !!staff.phone &&
        isValidPhone(staff.phone) &&
        (!staff.email || isValidEmail(staff.email))
      );
    } else if (step === 1) {
      return !!staff.position && !!staff.hireDate && !!staff.salary && isPositiveNumber(staff.salary);
    } else if (step === 2) {
      // Contract upload — optional
      return true;
    } else if (step === 3) {
      // Notes — optional
      return true;
    }
    return false;
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Card variant="outlined" sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(67,56,202,0.07)', overflow: 'hidden', border: '1px solid #E5E7EB' }}>
            <StepCardHeader
              icon={<Person sx={{ color: '#fff', fontSize: 22 }} />}
              title="معلومات شخصية"
              subtitle="بيانات هوية الموظف الشخصية"
              color="linear-gradient(135deg, #4338CA, #5B21F4)"
              bg="linear-gradient(135deg, #EEF2FF 0%, #E0F2FE 100%)"
              borderColor="rgba(67,56,202,0.14)"
            />
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: '16px' }}>
                <TextField
                  fullWidth
                  label="رقم الهوية *"
                  name="idNumber"
                  value={staff.idNumber}
                  onChange={handleChange}
                  error={!!employeeIdError || (!!errors.idNumber && triedNext)}
                  helperText={employeeIdError || ((triedNext && errors.idNumber) ? errors.idNumber : '9 أرقام')}
                  inputProps={{ maxLength: 9 }}
                  InputProps={{
                    sx: { borderRadius: '10px' },
                    startAdornment: <Badge color="action" sx={{ mr: 1 }} />,
                    endAdornment: employeeIdValidating && <CircularProgress size={20} />,
                  }}
                  sx={fieldSx}
                />
                <TextField
                  fullWidth
                  label="الاسم الأول *"
                  name="firstName"
                  value={staff.firstName}
                  onChange={handleChange}
                  error={!!errors.firstName && triedNext}
                  helperText={(triedNext && errors.firstName) ? errors.firstName : ' '}
                  InputProps={{ sx: { borderRadius: '10px' } }}
                  sx={fieldSx}
                />
                <TextField
                  fullWidth
                  label="اسم العائلة *"
                  name="lastName"
                  value={staff.lastName}
                  onChange={handleChange}
                  error={!!errors.lastName && triedNext}
                  helperText={(triedNext && errors.lastName) ? errors.lastName : ' '}
                  InputProps={{ sx: { borderRadius: '10px' } }}
                  sx={fieldSx}
                />
                <TextField
                  fullWidth
                  type="date"
                  label="تاريخ الميلاد *"
                  name="birthDate"
                  value={staff.birthDate}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                  error={!!errors.birthDate && triedNext}
                  helperText={(triedNext && errors.birthDate) ? errors.birthDate : ' '}
                  InputProps={{ sx: { borderRadius: '10px' } }}
                  sx={fieldSx}
                />

                {/* Phone */}
                <Box sx={{ display: 'flex', gap: 1, minWidth: 0 }}>
                  <TextField
                    select
                    label="رمز"
                    name="phoneCode"
                    value={staff.phoneCode}
                    onChange={handleChange}
                    helperText=" "
                    sx={{ width: 100, flexShrink: 0, ...fieldSx }}
                    InputProps={{ sx: { borderRadius: '10px' } }}
                    SelectProps={{ MenuProps: { PaperProps: { sx: { minWidth: 130 } } } }}
                  >
                    {PHONE_CODE_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <span>{option.flag}</span>
                          <span style={{ fontSize: '0.8rem' }}>{option.label}</span>
                        </Box>
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    label="رقم الهاتف *"
                    name="phone"
                    value={staff.phone}
                    onChange={handleChange}
                    error={!!errors.phone && triedNext}
                    helperText={(triedNext && errors.phone) ? errors.phone : ' '}
                    InputProps={{
                      sx: { borderRadius: '10px' },
                      startAdornment: <Phone color="action" sx={{ mr: 0.5 }} />,
                    }}
                    sx={{ flex: 1, minWidth: 0, ...fieldSx }}
                    placeholder="0501234567"
                  />
                </Box>

                <TextField
                  fullWidth
                  label="البريد الإلكتروني (اختياري)"
                  name="email"
                  value={staff.email}
                  onChange={handleChange}
                  type="email"
                  error={!!errors.email}
                  helperText={errors.email || ' '}
                  InputProps={{ sx: { borderRadius: '10px' } }}
                  sx={fieldSx}
                  placeholder="example@email.com"
                />
              </Box>
            </CardContent>
          </Card>
        );
      case 1:
        return (
          <Card variant="outlined" sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(67,56,202,0.07)', overflow: 'hidden', border: '1px solid #E5E7EB' }}>
            <StepCardHeader
              icon={<Work sx={{ color: '#fff', fontSize: 22 }} />}
              title="معلومات وظيفية"
              subtitle="الوظيفة، تاريخ التعيين، والراتب"
              color="linear-gradient(135deg, #16A34A, #22C55E)"
              bg="linear-gradient(135deg, #DCFCE7 0%, #ECFDF5 100%)"
              borderColor="rgba(22,163,74,0.14)"
            />
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: '16px' }}>
                <TextField
                  fullWidth
                  select
                  label="الوظيفة *"
                  name="position"
                  value={staff.position}
                  onChange={handleChange}
                  error={!!errors.position && triedNext}
                  helperText={(triedNext && errors.position) ? errors.position : ' '}
                  InputProps={{ sx: { borderRadius: '10px' } }}
                  sx={fieldSx}
                  SelectProps={{ MenuProps: { PaperProps: { sx: { maxHeight: 250 } } } }}
                >
                  {employeeTypes.map((pos) => (
                    <MenuItem key={pos.id} value={pos.id}>{pos.display_value}</MenuItem>
                  ))}
                </TextField>
                <TextField
                  fullWidth
                  type="date"
                  label="تاريخ التعيين *"
                  name="hireDate"
                  value={staff.hireDate}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                  error={!!errors.hireDate && triedNext}
                  helperText={(triedNext && errors.hireDate) ? errors.hireDate : ' '}
                  InputProps={{ sx: { borderRadius: '10px' } }}
                  sx={fieldSx}
                />
                <TextField
                  fullWidth
                  label="الراتب الأساسي *"
                  name="salary"
                  value={staff.salary}
                  onChange={handleChange}
                  error={!!errors.salary && triedNext}
                  helperText={(triedNext && errors.salary) ? errors.salary : ' '}
                  inputProps={{ min: 0, type: 'number' }}
                  InputProps={{ sx: { borderRadius: '10px' } }}
                  sx={fieldSx}
                />
              </Box>
            </CardContent>
          </Card>
        );
      case 2:
        return (
          <Card variant="outlined" sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(67,56,202,0.07)', overflow: 'hidden', border: '1px solid #E5E7EB' }}>
            <StepCardHeader
              icon={<AttachFile sx={{ color: '#fff', fontSize: 22 }} />}
              title="تحميل العقد"
              subtitle="ملف عقد الموظف (اختياري)"
              color="linear-gradient(135deg, #0284C7, #00BFFF)"
              bg="linear-gradient(135deg, #E0F2FE 0%, #F0F9FF 100%)"
              borderColor="rgba(2,132,199,0.14)"
            />
            <CardContent sx={{ p: 3 }}>
              <Box sx={{
                border: '2px dashed #0284C7',
                borderRadius: 3,
                p: 4,
                textAlign: 'center',
                backgroundColor: 'rgba(2,132,199,0.03)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: 'rgba(2,132,199,0.07)',
                  borderColor: '#0369A1'
                }
              }}>
                <CloudUpload sx={{ fontSize: 40, color: '#0284C7', mb: 1 }} />
                <Box>
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<CloudUpload />}
                    sx={{
                      mb: 2,
                      borderRadius: 2.5,
                      textTransform: 'none',
                      fontWeight: 700,
                      fontFamily: 'Cairo, Tajawal, sans-serif',
                      borderColor: '#0284C7',
                      color: '#0284C7',
                      '&:hover': {
                        borderColor: '#0369A1',
                        backgroundColor: 'rgba(2,132,199,0.08)'
                      }
                    }}
                  >
                    تحميل ملف العقد (PDF)
                    <input
                      type="file"
                      name="contractFile"
                      accept="application/pdf"
                      hidden
                      onChange={handleChange}
                    />
                  </Button>
                </Box>
                <Typography variant="body2" sx={{ mb: 1, color: '#6B7280', fontFamily: 'Cairo, Tajawal, sans-serif' }}>
                  يمكن رفع ملف العقد بصيغة PDF فقط
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                  {staff.contractFile && <CheckCircle sx={{ fontSize: 18, color: '#16A34A' }} />}
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: 'Cairo, Tajawal, sans-serif',
                      fontWeight: staff.contractFile ? 700 : 400,
                      color: staff.contractFile ? '#16A34A' : '#6B7280',
                    }}
                  >
                    {staff.contractFile ? staff.contractFile.name : 'لم يتم اختيار ملف'}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        );
      case 3:
        return (
          <Card variant="outlined" sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(67,56,202,0.07)', overflow: 'hidden', border: '1px solid #E5E7EB' }}>
            <StepCardHeader
              icon={<NoteAdd sx={{ color: '#fff', fontSize: 22 }} />}
              title="ملاحظات"
              subtitle="أي معلومات إضافية عن الموظف (اختياري)"
              color="linear-gradient(135deg, #5B21F4, #7C3AED)"
              bg="linear-gradient(135deg, #EDE9FE 0%, #F5F3FF 100%)"
              borderColor="rgba(91,33,244,0.14)"
            />
            <CardContent sx={{ p: 3 }}>
              <TextField
                fullWidth
                label="ملاحظات"
                name="notes"
                value={staff.notes}
                onChange={handleChange}
                multiline
                rows={7}
                InputProps={{ sx: { borderRadius: '10px' } }}
                sx={fieldSx}
                placeholder="أضف أي ملاحظات خاصة بالموظف هنا..."
              />
            </CardContent>
          </Card>
        );
      default:
        return 'خطأ غير معروف';
    }
  };

  const progressPercent = Math.round(((activeStep) / steps.length) * 100);

  return (
    // 🎨 Design System Applied
    <Box
      sx={{
        direction: 'rtl',
        fontFamily: 'Cairo, Tajawal, sans-serif',
        p: { xs: 2, md: 3 },
        bgcolor: '#F8F9FF',
        minHeight: '100vh',
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: 'easeOut' }}
      >
        <Box sx={{ maxWidth: 920, mx: 'auto' }}>
          {/* Hero Header */}
          <MotionPaper
            component={motion.div}
            elevation={0}
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            sx={{
              borderRadius: '24px 24px 0 0',
              background: 'linear-gradient(135deg, #00BFFF 0%, #4338CA 60%, #5B21F4 100%)',
              p: { xs: 2.5, sm: '28px 36px' },
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {/* decorative shapes */}
            <Box sx={{ position: 'absolute', top: -50, right: -50, width: 200, height: 200, borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.08)' }} />
            <Box sx={{ position: 'absolute', bottom: -60, left: -30, width: 180, height: 180, borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.06)' }} />
            <Box sx={{ position: 'absolute', top: 10, left: '40%', width: 80, height: 80, borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.05)' }} />

            <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative', gap: 2 }}>
              {/* Back button */}
              <Tooltip title="العودة إلى قائمة الموظفين">
                <IconButton
                  onClick={handleCancel}
                  sx={{
                    color: '#fff',
                    backgroundColor: 'rgba(255,255,255,0.15)',
                    border: '1px solid rgba(255,255,255,0.25)',
                    backdropFilter: 'blur(6px)',
                    flexShrink: 0,
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.25)', borderColor: 'rgba(255,255,255,0.5)' },
                  }}
                >
                  <ArrowBack />
                </IconButton>
              </Tooltip>

              {/* Title center */}
              <Box sx={{ flexGrow: 1, textAlign: 'center' }}>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
                  <Box sx={{
                    width: 44, height: 44, borderRadius: '12px',
                    backgroundColor: 'rgba(255,255,255,0.15)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    backdropFilter: 'blur(4px)',
                  }}>
                    <PersonAddAlt1 sx={{ color: '#fff', fontSize: 24 }} />
                  </Box>
                  <Typography variant="h5" sx={{ color: '#fff', fontWeight: 800, letterSpacing: 0.3, fontFamily: 'Cairo, Tajawal, sans-serif', fontSize: { xs: '1.4rem', sm: '1.75rem' } }}>
                    إضافة موظف جديد
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.85rem', fontFamily: 'Cairo, Tajawal, sans-serif' }}>
                  {steps[activeStep]} — الخطوة {activeStep + 1} من {steps.length}
                </Typography>
              </Box>

              {/* Step counter pill */}
              <Box sx={{
                textAlign: 'center', flexShrink: 0,
                backgroundColor: 'rgba(255,255,255,0.15)',
                border: '1px solid rgba(255,255,255,0.25)',
                borderRadius: '12px', px: 2, py: 0.75,
                backdropFilter: 'blur(6px)',
              }}>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.75)', display: 'block', lineHeight: 1.1, fontSize: '0.68rem', fontFamily: 'Cairo, Tajawal, sans-serif' }}>
                  الاكتمال
                </Typography>
                <Typography variant="h6" sx={{ color: '#fff', fontWeight: 800, lineHeight: 1.2, fontFamily: 'Cairo, Tajawal, sans-serif' }}>
                  {progressPercent}%
                </Typography>
              </Box>
            </Box>
          </MotionPaper>

          {/* Animated progress bar */}
          <Box sx={{ height: 5, background: 'rgba(67,56,202,0.12)' }}>
            <Box sx={{
              height: '100%',
              width: `${progressPercent}%`,
              background: 'linear-gradient(90deg, #00BFFF 0%, #5B21F4 100%)',
              transition: 'width 0.5s cubic-bezier(0.4,0,0.2,1)',
              borderRadius: '0 3px 3px 0',
              boxShadow: '0 0 8px rgba(91,33,244,0.5)',
            }} />
          </Box>

          {/* Main card */}
          <MotionPaper
            component={motion.div}
            elevation={0}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: 0.1, ease: 'easeOut' }}
            sx={{
              borderRadius: '0 0 24px 24px',
              p: { xs: 2, sm: 4 },
              bgcolor: '#fff',
              border: '1px solid #E5E7EB',
              borderTop: 'none',
              boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
            }}
          >
            {/* Stepper */}
            <Box sx={{
              backgroundColor: '#F8F9FF',
              borderRadius: 3,
              pt: 2.5,
              pb: 1.5,
              px: 1,
              mb: 4,
              border: '1px solid #E5E7EB',
            }}>
              <Stepper
                activeStep={activeStep}
                alternativeLabel
                connector={<ColorlibConnector />}
              >
                {steps.map((label, index) => (
                  <Step key={label} completed={activeStep > index}>
                    <StepLabel StepIconComponent={ColorlibStepIcon}>
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: activeStep === index ? 700 : 500,
                          fontSize: activeStep === index ? '0.8rem' : '0.72rem',
                          fontFamily: 'Cairo, Tajawal, sans-serif',
                          color: activeStep === index
                            ? '#4338CA'
                            : activeStep > index
                              ? '#16A34A'
                              : '#9CA3AF',
                          transition: 'all 0.2s ease',
                        }}
                      >
                        {label}
                      </Typography>
                      {activeStep > index && (
                        <Typography variant="caption" sx={{ color: '#16A34A', fontSize: '0.65rem', display: 'block', mt: -0.3, fontFamily: 'Cairo, Tajawal, sans-serif' }}>
                          ✓ مكتمل
                        </Typography>
                      )}
                    </StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Box>

            <form onSubmit={handleSubmit}>
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeStep}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  variants={pageVariants}
                  transition={{ duration: 0.3 }}
                >
                  {getStepContent(activeStep)}
                </motion.div>
              </AnimatePresence>

              {/* Navigation */}
              <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mt: 4,
                pt: 3,
                borderTop: '1px solid #E5E7EB',
              }}>
                {/* Back */}
                <Button
                  variant="outlined"
                  onClick={handleBack}
                  disabled={activeStep === 0 || loading}
                  startIcon={<KeyboardArrowRight />}
                  sx={{
                    borderRadius: 2.5,
                    px: 3, py: 1,
                    fontWeight: 700,
                    textTransform: 'none',
                    fontFamily: 'Cairo, Tajawal, sans-serif',
                    borderColor: '#C7D2FE',
                    color: '#4338CA',
                    borderWidth: '1.5px',
                    '&:hover': { borderWidth: '1.5px', borderColor: '#4338CA', backgroundColor: '#EEF2FF' },
                    '&.Mui-disabled': { borderStyle: 'dashed' },
                  }}
                >
                  رجوع
                </Button>

                {/* Step pills */}
                <Box sx={{ display: 'flex', gap: 0.6, alignItems: 'center' }}>
                  {steps.map((_, idx) => (
                    <Box key={idx} sx={{
                      width: idx === activeStep ? 28 : 8,
                      height: 8,
                      borderRadius: 10,
                      backgroundColor: idx < activeStep
                        ? '#16A34A'
                        : idx === activeStep
                          ? '#4338CA'
                          : '#E5E7EB',
                      transition: 'all 0.35s cubic-bezier(0.4,0,0.2,1)',
                      boxShadow: idx === activeStep ? '0 0 6px rgba(67,56,202,0.5)' : 'none',
                    }} />
                  ))}
                </Box>

                {/* Next / Submit */}
                <Box>
                  {activeStep < steps.length - 1 ? (
                    <Button
                      variant="contained"
                      onClick={handleNext}
                      disabled={loading || (activeStep === 0 && (employeeIdValidating || !!employeeIdError))}
                      endIcon={employeeIdValidating ? <CircularProgress size={16} color="inherit" /> : <KeyboardArrowLeft />}
                      sx={{
                        borderRadius: 2.5,
                        px: 3.5, py: 1,
                        fontWeight: 700,
                        textTransform: 'none',
                        fontFamily: 'Cairo, Tajawal, sans-serif',
                        bgcolor: '#4338CA',
                        boxShadow: 'none',
                        '&:hover': {
                          bgcolor: '#3730A3',
                          boxShadow: 'none',
                        },
                        '&.Mui-disabled': { bgcolor: '#E5E7EB', color: '#9CA3AF' },
                      }}
                    >
                      {employeeIdValidating ? 'جاري التحقق...' : 'التالي'}
                    </Button>
                  ) : (
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={handleCancel}
                        disabled={loading}
                        startIcon={<Cancel />}
                        sx={{
                          borderRadius: 2.5, px: 2.5, py: 1,
                          fontWeight: 700, borderWidth: '1.5px',
                          textTransform: 'none',
                          fontFamily: 'Cairo, Tajawal, sans-serif',
                          borderColor: '#DC2626', color: '#DC2626',
                          '&:hover': { borderWidth: '1.5px', borderColor: '#DC2626', backgroundColor: '#FEE2E2' },
                        }}
                      >
                        إلغاء
                      </Button>
                      <Button
                        variant="contained"
                        type="submit"
                        disabled={loading || !isStepComplete(activeStep) || !!employeeIdError}
                        startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <Save />}
                        sx={{
                          borderRadius: 2.5, px: 3.5, py: 1,
                          fontWeight: 700,
                          textTransform: 'none',
                          fontFamily: 'Cairo, Tajawal, sans-serif',
                          bgcolor: '#4338CA',
                          boxShadow: 'none',
                          '&:hover': {
                            bgcolor: '#3730A3',
                            boxShadow: 'none',
                          },
                          '&.Mui-disabled': { bgcolor: '#E5E7EB', color: '#9CA3AF' },
                        }}
                      >
                        {loading ? 'جارٍ الحفظ...' : 'حفظ الموظف'}
                      </Button>
                    </Box>
                  )}
                </Box>
              </Box>
            </form>
          </MotionPaper>
        </Box>
      </motion.div>
    </Box>
  );
};

export default CreateStaffPage;
